import * as React from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { Card } from "@mantine/core";
import { bindActionCreators } from "redux";
import { getMyAssociationResources } from "../../data/actions/associationActions";
import { IState } from "../../data/reducers/state";
import {
  IAssociation,
  IAssociationResource,
} from "../../types/association/Association";
import { Role } from "../../types/persons/Role";
import { IPerson } from "../../types/persons/person";
import { AvailabilitiesDashboard } from "../availabilities/AvailabilitiesDashboard";
// other
import { CenteredLoader } from "../common/loader";
import { ITabBaseState, TabBase } from "../common/tabs/TabBase";
import PersonAvatar from "../person/PersonAvatar";
import AppointmentsHistory from "./AppointmentsHistory";
import AssociationResourceTab from "./AssociationResource";
// tabs
import PaymentHistoryView from "./PaymentHistoryView";
import { ProfileDashboard } from "./ProfileDashboard";
import { IProfileTabProps } from "./ProfileTabType";

interface IPersonalProfilePageProps {
  actions: {
    getMyAssociationResources: Function;
  };
  person: IPerson;
  hideDashboard?: boolean
  hideTimesheet?: boolean
  associationsMap: { [key: string]: IAssociation };
  associationResourcesMap: { [key: string]: IAssociationResource[] };
  defaultTab?: string;

}

interface IPersonalProfilePageState extends ITabBaseState { }

export class _ProfilePage extends TabBase<
  IPersonalProfilePageProps,
  IPersonalProfilePageState,
  IProfileTabProps
> {
  constructor(props: IPersonalProfilePageProps, content) {
    super(props, content);

    this.state = {
      tab: props.defaultTab || (props.hideDashboard ? "Appointments" : "Dashboard"),
    };

    this.props.actions
      .getMyAssociationResources()
      .catch((er) => alert(`Error loading association resources: ${er}`));
  }

  getTabs(): { [tab: string]: React.ElementType<IProfileTabProps> } {
    const assocResourceIds = new Set(
      Object.keys(this.props?.associationResourcesMap || {}) || [],
    );
    const associationResources: string[] =
      this.props.person.associations
        ?.filter((a) => assocResourceIds.has(a.associationId))
        .map((a) => a.associationId) || [];
    const associationResourceComponents = associationResources.reduce(
      (prev, assocId) => ({
        ...prev,
        [`${this.props.associationsMap[assocId]?.name || assocId} resources`]: (
          props,
        ) => (
          <AssociationResourceTab
            {...props}
            key={assocId}
            associationId={assocId}
          >
            {JSON.stringify(props)}
          </AssociationResourceTab>
        ),
      }),
      {},
    );

    let options: { [key: string]: React.ElementType<IProfileTabProps> } = {}
    if (!this.props.hideDashboard) {
      options["Dashboard"] = ProfileDashboard;
    }
    options['Appointments'] = AppointmentsHistory;

    const usesAvailabilities = this.props.person?.associations?.some(
      (a) =>
        this.props.associationsMap[a?.associationId]?.config?.availabilities
          ?.enabled,
    );
    if (usesAvailabilities) {
      options.Availabilities = AvailabilitiesDashboard;
    } else if (this.state.tab == "Availabilities") {
      this.setState({ tab: "Dashboard" });
    }

    // is a manager in any association
    options["Payment History"] = PaymentHistoryView


    options = { ...options, ...associationResourceComponents };

    return options;
  }

  render() {
    const p = this.props.person;
    if (!p) return <CenteredLoader />;
    const associations = Array.from(
      new Set<string>(
        this.props.person.associations?.map((a) => a.associationId) || [],
      ),
    )
      .map((a) => this.props.associationsMap[a])
      .filter((a) => !!a);

    const roles = (this.props.person?.associations || []).reduce(
      (prev, a) => prev | a.role,
      0,
    );

    const subtitleFields = [
      p.externalIdentifier,
      ...associations.map((a) => a.name),
      ...Role.getRoleNames(roles),
    ].filter((f) => !!f);

    return (
      <Card style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
        <div padding="md">
          <div style={{ width: "19%", float: "left" }}>
            <PersonAvatar
              image={p.image}
              size="medium"
              style={{ maxWidth: "100%", height: "100%" }}
            />
          </div>
          <div style={{ width: "78%", marginLeft: "22%" }}>
            <h2>{p.fullName}</h2>
            <p className="grey" style={{}}>
              {subtitleFields.join(" · ")}
            </p>
          </div>
        </div>

        {this.getTabView({ person: this.props.person })}
      </Card>
    );
  }
}

function mapImpersonalStateToProps(state: IState, ownProps) {
  return {
    associationsMap: state.associations.map,
    associationResourcesMap: state.associations.resources,
  };
}

function mapPersonalStateToProps(state: IState, ownProps) {
  return {
    person: state.persons.map[state.persons.self],
    associationsMap: state.associations.map,
    associationResourcesMap: state.associations.resources,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ getMyAssociationResources }, dispatch),
  };
}

export const ProfilePage = connect(
  mapImpersonalStateToProps,
  mapDispatchToProps,
)(_ProfilePage);

const PersonalProfilePage = connect(
  mapPersonalStateToProps,
  mapDispatchToProps,
)(_ProfilePage);

function RoutedPersonalProfilePage(props) {
  const { section } = useParams();
  return <PersonalProfilePage {...props} defaultTab={section} />;
}

export default PersonalProfilePage
